import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Alert,
  Skeleton,
  Grid,
  Stack,
} from "@mui/material";
import {
  EmojiEvents as EmojiEventsIcon,
  TrendingUp,
  TrendingDown,
  Remove,
} from "@mui/icons-material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import apiFetch from "services/http";
import { PageToolbar, EmptyState } from "components/Convokko";

const getTeamName = (fullName) => {
  if (!fullName) return "";
  const parts = fullName.split(" - ");
  return parts.length > 1 ? parts[1] : parts[0];
};

const getDiferencia = (tf, tc) => parseInt(tf || 0, 10) - parseInt(tc || 0, 10);

const positionStyle = (index, puntos) => {
  if (index === 0) return { backgroundColor: "#FFD700", color: "#000" };
  if (index === 1) return { backgroundColor: "#C0C0C0", color: "#000" };
  if (index === 2) return { backgroundColor: "#CD7F32", color: "#fff" };
  if (parseInt(puntos || 0, 10) === 0) return { backgroundColor: "#F44335", color: "#fff" };
  return { backgroundColor: "#22336B", color: "#fff" };
};

export default function Clasificacion() {
  const [datosCompletos, setDatosCompletos] = useState([]);
  const [equipoIdx, setEquipoIdx] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await apiFetch("/api/externas/clasificacion");
        if (data?.status === "success" && Array.isArray(data.data)) {
          setDatosCompletos(data.data);
          if (data.data.length > 0) setEquipoIdx("0");
        } else {
          setError(data?.message || "Error desconocido al cargar la clasificación.");
        }
      } catch (err) {
        setError(err.message || "Error al cargar la clasificación.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const clasificacionActual = useMemo(() => {
    const item = datosCompletos[Number(equipoIdx)];
    return item?.clasificacion || [];
  }, [datosCompletos, equipoIdx]);

  const diferenciaVisual = (dg) => {
    const color = dg > 0 ? "success.main" : dg < 0 ? "error.main" : "text.secondary";
    const Icon = dg > 0 ? TrendingUp : dg < 0 ? TrendingDown : Remove;
    return (
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.25}>
        <Icon sx={{ fontSize: 16, color }} />
        <Typography variant="body2" fontWeight={700} sx={{ color }}>
          {dg > 0 ? `+${dg}` : dg}
        </Typography>
      </Stack>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3}>
        <PageToolbar
          icon={EmojiEventsIcon}
          title="Clasificación"
          count={clasificacionActual.length}
          countLabel="equipos en la liga"
          showSeason={false}
          filters={[
            {
              id: "equipo",
              label: "Equipo",
              allLabel: "Selecciona equipo",
              value: equipoIdx,
              onChange: setEquipoIdx,
              options: datosCompletos.map((item, index) => ({
                value: String(index),
                label: item.equipo,
              })),
              minWidth: 190,
            },
          ]}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Grid container spacing={2}>
            {[...Array(8)].map((_, i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <Skeleton variant="rounded" height={180} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
        ) : clasificacionActual.length === 0 ? (
          <EmptyState
            icon={EmojiEventsIcon}
            title="Sin datos de clasificación"
            subtitle="Todavía no hay clasificación disponible. En cuanto el equipo esté registrado en el IMD, se mostrará aquí."
          />
        ) : (
          <Grid container spacing={2}>
            {clasificacionActual.map((equipo, index) => {
              const dg = getDiferencia(equipo.TF, equipo.TC);
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={`${equipo.Equipo}-${index}`}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      height: "100%",
                      border: "1px solid",
                      borderColor: "grey.200",
                      transition: "border-color .15s ease",
                      "&:hover": { borderColor: "grey.400" },
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
                        <Chip
                          label={index + 1}
                          size="small"
                          sx={{
                            ...positionStyle(index, equipo.Puntos),
                            fontWeight: 800,
                            minWidth: 34,
                          }}
                        />
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          noWrap
                          sx={{ flexGrow: 1, minWidth: 0 }}
                        >
                          {getTeamName(equipo.Equipo)}
                        </Typography>
                        <Chip
                          label={`${equipo.Puntos} pts`}
                          size="small"
                          sx={{
                            background: "#F59E0B",
                            color: "#fff",
                            fontWeight: 800,
                          }}
                        />
                      </Stack>

                      <Grid container textAlign="center" mb={1.5}>
                        {[
                          { label: "PJ", value: equipo.PJ },
                          { label: "PG", value: equipo.PG, color: "#4caf50" },
                          { label: "PE", value: equipo.PE, color: "#ff9800" },
                          { label: "PP", value: equipo.PP, color: "#f44336" },
                        ].map((stat) => (
                          <Grid item xs={3} key={stat.label}>
                            <Typography variant="caption" color="text.secondary">
                              {stat.label}
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              sx={{ color: stat.color || "inherit" }}
                            >
                              {stat.value}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>

                      <Stack
                        direction="row"
                        justifyContent="space-around"
                        alignItems="center"
                        sx={{ bgcolor: "grey.100", borderRadius: 2.5, p: 1 }}
                      >
                        <Box textAlign="center">
                          <Typography variant="caption" color="text.secondary">
                            TF
                          </Typography>
                          <Typography variant="body2" fontWeight={700} color="info.main">
                            {equipo.TF}
                          </Typography>
                        </Box>
                        <Box textAlign="center">
                          <Typography variant="caption" color="text.secondary">
                            TC
                          </Typography>
                          <Typography variant="body2" fontWeight={700} color="error.main">
                            {equipo.TC}
                          </Typography>
                        </Box>
                        <Box textAlign="center">
                          <Typography variant="caption" color="text.secondary">
                            DG
                          </Typography>
                          {diferenciaVisual(dg)}
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
      <Footer />
    </DashboardLayout>
  );
}
